<template>
	<div class="flex flex-col gap-2 text-won-dark-blue! normal-case!">
		<div class="flex flex-col gap-1">
			<span class="text-[14px] font-medium text-won-black">Target Role</span>

			<Combobox
				:options="roleOptions"
				:default-value="specialization"
				placeholder="Select target role"
				combo-box-width="w-full"
				@value:update="(value) => (specialization = value)"
			/>
		</div>

		<label
			v-if="isOther"
			class="flex flex-col gap-1"
		>
			<span class="text-[14px] font-medium text-won-black">Your Role Title</span>

			<input
				v-model="title"
				type="text"
				maxlength="255"
				placeholder="e.g. Supply Chain Analyst"
				:class="inputStyle"
			/>
		</label>

		<div class="flex flex-col gap-1">
			<span class="text-[14px] font-medium text-won-black">Seniority Level</span>

			<Combobox
				:options="LEVEL_OPTIONS"
				:default-value="optionFor(LEVEL_OPTIONS, level)"
				display-key="label"
				:disable-typing="true"
				placeholder="Select seniority level"
				combo-box-width="w-full"
				@value:update="handleLevelUpdate"
			>
				<template #menuOption="{ option }">
					<span class="text-base text-won-dark-blue">{{ option.label }}</span>
				</template>
			</Combobox>
		</div>

		<div
			v-if="isAboveSenior"
			class="flex flex-col gap-1"
		>
			<span class="text-[14px] font-medium text-won-black">Track</span>

			<Combobox
				:options="TRACK_OPTIONS"
				:default-value="optionFor(TRACK_OPTIONS, track)"
				display-key="label"
				:disable-typing="true"
				placeholder="Select track..."
				combo-box-width="w-full"
				@value:update="handleTrackUpdate"
			>
				<template #menuOption="{ option }">
					<span class="text-base text-won-dark-blue">{{ option.label }}</span>
				</template>
			</Combobox>
		</div>

		<p
			v-if="errorMessage"
			class="text-[12px] text-won-red"
		>
			{{ errorMessage }}
		</p>

		<div class="mt-1 flex items-center justify-end gap-1">
			<button
				type="button"
				class="cursor-pointer rounded bg-won-sand-light px-2 py-1 text-[14px] font-medium text-won-black"
				@click="emit('cancel')"
			>
				Cancel
			</button>

			<button
				type="button"
				class="
					cursor-pointer rounded bg-won-green px-2 py-1 text-[14px] font-medium
					text-white disabled:cursor-not-allowed disabled:opacity-50
				"
				:disabled="! canSubmit || isSubmitting"
				@click="submit"
			>
				{{ isSubmitting ? 'Updating...' : 'Update Score' }}
			</button>
		</div>
	</div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import Combobox from '../library/combobox.vue';
import { getTargetTitles, setTargetSelection } from '../../api/resume-score';
import { useResumeScorerStore } from '../../store/scorer.store';
import { inputStyle } from '../../utils/styles';
import {
	canSubmitTargetSelection,
	isAboveSenior as isLevelAboveSenior,
	LEVEL_OPTIONS,
	OTHER_ROLE,
	TRACK_OPTIONS,
} from '../../utils/target-selection';

// EN-796: the target title/level/(track) selector — the "Option A" explicit
// pick that decides what a resume is scored against. Reads the covered-title
// catalog from the server rather than hardcoding it, since profiles are
// added/frozen over time.
//
// EN-814: "Other" swaps the catalog pick for a free-text title, which the
// server places at High/Medium/Low confidence like a detected role. The form
// opens pre-filled with whatever the panel already knows (a catalog
// specialization, or an "Other" title), and stays on "Updating..." until the
// new score is in, so the panel moves straight to the new role's treatment.
const props = defineProps({
	resumeUuid: {
		type: String,
		required: true,
	},
	initialSpecialization: {
		type: String,
		default: null,
	},
	initialTitle: {
		type: String,
		default: null,
	},
	initialLevel: {
		type: String,
		default: null,
	},
	initialTrack: {
		type: String,
		default: null,
	},
});

const emit = defineEmits(['selected', 'cancel']);

const scorerStore = useResumeScorerStore();

const titleOptions = ref([]);
const specialization = ref(props.initialTitle ? OTHER_ROLE : props.initialSpecialization);
const title = ref(props.initialTitle ?? '');
const level = ref(props.initialLevel);
const track = ref(isLevelAboveSenior(props.initialLevel) ? props.initialTrack : null);
const errorMessage = ref(null);
const isSubmitting = ref(false);

const roleOptions = computed(() => [...titleOptions.value, OTHER_ROLE]);
const isOther = computed(() => specialization.value === OTHER_ROLE);
const isAboveSenior = computed(() => isLevelAboveSenior(level.value));

const canSubmit = computed(() => canSubmitTargetSelection({
	specialization: specialization.value,
	title: title.value,
	level: level.value,
	track: track.value,
}));

const optionFor = (options, value) => options.find((option) => option.value === value) ?? null;

const handleLevelUpdate = (value) => {
	level.value = typeof value === 'object' ? value?.value : value;

	if (!isAboveSenior.value) {
		track.value = null;
	}
};

const handleTrackUpdate = (value) => {
	track.value = typeof value === 'object' ? value?.value : value;
};

const submit = async () => {
	errorMessage.value = null;
	isSubmitting.value = true;

	const role = isOther.value ? { title: title.value.trim() } : { specialization: specialization.value };

	try {
		await setTargetSelection(props.resumeUuid, {
			...role,
			level: level.value,
			track: isAboveSenior.value ? track.value : null,
		});
	} catch (error) {
		errorMessage.value = error?.response?.data?.message || 'Could not save your target role. Please try again.';
		isSubmitting.value = false;

		return;
	}

	await scorerStore.fetchScore();
	isSubmitting.value = false;
	emit('selected');
};

onMounted(async () => {
	try {
		titleOptions.value = await getTargetTitles();
	} catch {
		errorMessage.value = 'Could not load the list of job titles. Please try again.';
	}
});
</script>
